import * as React from 'react';
import { UserProvider } from '../../../shared/context/user-context';
import * as Features from '../../../shared/Features';
import { Translate } from '../../../shared/LanguageService';
import AdminPlanOrderForm from '../view/planOrders/admin/AdminPlanOrderForm';
import BasicOrderInfo from './basicorderinfo/BasicOrderInfoPage';
import ContactAddressPage from './contactaddress/ContactAddressPage';
import { createUploadHandlerFactory } from './videohandling/film/utils/uploadHelper';
import EditPage from './editorder/EditPage';
import LogListWithPager from '../../logs/LogListWithPager';
import MatterportHandling from './matterporthandling/MatterportHandling';
import PlanOrderForm from './planOrders/agent/PlanOrderForm';
import TextHandling from './texthandling/TextHandling';
import VideoHandling from './videohandling/VideoHandling';
import SEPlayHandling from './seplayhandling/SEPlayHandling';
import * as ProductProperty from '../../../shared/ProductProperties';
import ShowcaseProccessingPage from './showcaseHandling/ShowcaseProccessingPage';
import { AuthenticatedUser } from '../../../shared/types';
import DigitalStylingCustomerViewDelegate from './digitalStylingOrder/customerView/view/DigitalStylingCustomerViewDelegate';
import { ImageProcessingPageDelegate } from './imageprocessing/ImageProcessingPageDelegate';

export const BasicInfoTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('basic');
    }, []);

    if (
        Features.hasAnyFeature([Features.ViewBasicOrderInfo, Features.ViewStylingOrder], props.user)
    ) {
        return (
            <fieldset>
                <BasicOrderInfo orderId={props.orderId} />
                {Features.hasAnyFeature(
                    [Features.ViewOrderContactInfo, Features.ViewStylingOrder],
                    props.user
                ) && (
                    <ContactAddressPage
                        orderId={props.orderId}
                        isArchived={props.photoIsArchived || props.planIsArchived}
                    />
                )}
            </fieldset>
        );
    } else {
        return null;
    }
};

type DigitalStylingTabProps = {
    orderId: number;
    user: AuthenticatedUser;
    isNewOrder: boolean;
    setActiveTab: (tab: string) => void;
};

export function DigitalStylingTab({
    orderId,
    user,
    isNewOrder,
    setActiveTab
}: DigitalStylingTabProps) {
    React.useEffect(() => {
        setActiveTab('digital-styling');
    }, []);

    let isCustomer = user.details.isCustomer;

    return (
        <DigitalStylingCustomerViewDelegate
            isCustomer={isCustomer}
            isNewOrder={isNewOrder}
            orderId={orderId}
            userRole={user.details.role}
        />
    );
}

export const EditTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('edit');
    }, []);

    return (
        <>
            <fieldset>
                <EditPage orderId={props.orderId} />
            </fieldset>
        </>
    );
};

export const PlanOrderTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('plan');
    }, []);

    return (
        <>
            {!props.planIsArchived &&
                (Features.hasFeature(Features.ViewPlanOrder, props.user) ||
                    (props.isMeasured &&
                        Features.hasFeature(Features.ViewPlanOrderWhenMeasured, props.user))) && (
                    <fieldset>
                        <legend>{Translate('view-order/tab/plan-order')}</legend>
                        <PlanOrderForm orderId={props.orderId} />
                    </fieldset>
                )}
            {!props.planIsArchived &&
                !props.loading &&
                Features.hasFeature(Features.ViewAdminPlanOrder, props.user) && (
                    <fieldset>
                        <legend>{Translate('view-order/tab/plan-order')}</legend>
                        <AdminPlanOrderForm orderId={props.orderId} isMeasured={props.isMeasured} />
                    </fieldset>
                )}
        </>
    );
};

export const LogsTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('logs');
    }, []);

    return (
        <>
            {!props.loading && Features.hasFeature(Features.ViewLogs, props.user) && (
                <fieldset>
                    <legend>{Translate('view-order/tab/logs')}</legend>
                    <LogListWithPager id={props.orderId} type="OrderId" />
                </fieldset>
            )}
        </>
    );
};

export const VideoTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('video');
    }, []);

    let createUploadHandler = createUploadHandlerFactory();

    return (
        <>
            {!props.photoIsArchived &&
                props.order.productProperties &&
                props.order.productProperties.some(
                    (property) => property.value === ProductProperty.Film
                ) && (
                    <fieldset>
                        <legend>
                            {Translate('view-order/tab/image-processing/legend/video-processing')}
                        </legend>
                        <UserProvider user={props.user}>
                            <VideoHandling
                                orderId={props.orderId}
                                createUploadHandler={createUploadHandler}
                                isFilmSent={props.order.isFilmSent}
                            />
                        </UserProvider>
                    </fieldset>
                )}
        </>
    );
};

type MatterportTabProps = {
    orderId: number;
    user: AuthenticatedUser;
    isNewOrder: boolean;
    setActiveTab: (tab: string) => void;
    order: any;
    photoIsArchived: boolean;
};

export const MatterportTab = (props: MatterportTabProps) => {
    React.useEffect(() => {
        props.setActiveTab('matterport');
    }, []);
    return (
        <>
            {!props.photoIsArchived &&
                props.order.productProperties &&
                props.order.productProperties.some(
                    (property) => property.value === ProductProperty.Matterport
                ) && (
                    <fieldset>
                        <legend>
                            {Translate(
                                'view-order/tab/image-processing/legend/matterport-processing'
                            )}
                        </legend>
                        <MatterportHandling
                            orderId={props.orderId}
                            orderMatterportLinkData={props.order.matterportLink}
                            brokerAgentEmail={props.order.brokerAgentEmail}
                            user={props.user}
                        />
                    </fieldset>
                )}
        </>
    );
};

export const SePlayTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('seplay');
    }, []);

    return (
        <>
            {props.order.productProperties &&
            props.order.productProperties.some(
                (property) => property.value === ProductProperty.SEPlay
            ) ? (
                <fieldset>
                    <legend>
                        {Translate('view-order/tab/image-processing/legend/se-play-processing')}
                    </legend>

                    <UserProvider user={props.user}>
                        <SEPlayHandling {...props} />
                    </UserProvider>
                </fieldset>
            ) : null}
        </>
    );
};

export const TextTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('text');
    }, []);

    return (
        <>
            {!props.photoIsArchived &&
            props.order.productProperties &&
            props.order.productProperties.some(
                (property) => property.value === ProductProperty.Text
            ) ? (
                <fieldset>
                    <legend>
                        {Translate('view-order/tab/image-processing/legend/text-processing')}
                    </legend>
                    <TextHandling orderId={props.orderId} />
                </fieldset>
            ) : null}
        </>
    );
};
export const ShowcaseTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('showcase');
    }, []);

    return (
        <>
            {props.order.productProperties &&
            props.order.productProperties.some(
                (property) => property.value === ProductProperty.Showcase
            ) ? (
                <fieldset>
                    <legend>{Translate('view-order/tab/showcase/legend/showcase')}</legend>
                    <ShowcaseProccessingPage
                        orderId={props.orderId}
                        actions={null}
                        showcaseOrder={null}
                    ></ShowcaseProccessingPage>
                </fieldset>
            ) : null}
        </>
    );
};

export const ImageTab = (props: any) => {
    React.useEffect(() => {
        props.setActiveTab('image');
    }, []);

    return (
        <>
            {!props.photoIsArchived && !props.loading && (
                <fieldset>
                    <legend>
                        {Translate('view-order/tab/image-processing/legend/image-processing')}
                    </legend>
                    <ImageProcessingPageDelegate
                        orderId={props.orderId}
                        productProperties={props.order.productProperties}
                        allowStitchingOfEditingImages={props.order.allowStitchingOfEditingImages}
                        orderType={props.orderType}
                        reloadData={props.reloadData}
                        user={props.user}
                    />
                </fieldset>
            )}
        </>
    );
};
